import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { DischargedReengagementConfig } from '../types/dischargedReengagement';

/**
 * Discharged re-engagement config hook — reads + writes
 * companies/{c}/discharged_reengagement_config/settings via the admin-gated
 * getReengagementConfig / updateReengagementConfig callables (RBAC enforced
 * server-side: super_admin + administrator only).
 *
 * Mirrors useClientCareConfig. The read callable returns a fully-resolved config
 * (defaults merged) plus `exists` so the page can show "using defaults" before
 * first save. The write callable returns only `{ ok }`, so on success we keep
 * the submitted config locally.
 */

interface GetConfigResponse {
  config: DischargedReengagementConfig;
  exists: boolean;
}

interface UpdateConfigResponse {
  ok: boolean;
}

export function useReengagementConfig(companyId?: string) {
  const [config, setConfig] = useState<DischargedReengagementConfig | null>(null);
  const [exists, setExists] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!companyId) {
      setConfig(null);
      setExists(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ companyId: string }, GetConfigResponse>(
        functions,
        'getReengagementConfig',
      );
      const res = await fn({ companyId });
      setConfig(res.data.config ?? null);
      setExists(!!res.data.exists);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load re-engagement config');
      console.error('[useReengagementConfig] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  /** Persist a partial or full config (server merge-writes). Returns true on success. */
  const save = useCallback(
    async (next: Partial<DischargedReengagementConfig>): Promise<boolean> => {
      if (!companyId) return false;
      setSaving(true);
      setError(null);
      try {
        const fn = httpsCallable<
          { companyId: string; config: Partial<DischargedReengagementConfig> },
          UpdateConfigResponse
        >(functions, 'updateReengagementConfig');
        await fn({ companyId, config: next });
        setConfig((prev) => (prev ? { ...prev, ...next } : (next as DischargedReengagementConfig)));
        setExists(true);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save re-engagement config');
        console.error('[useReengagementConfig] save failed:', e);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { config, exists, loading, saving, error, refetch, save };
}
